import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const RewardsProgram = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".rewards-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
      }
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-6">🎁</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Rewards Program
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Earn points with every purchase and unlock exclusive rewards, discounts, and special perks.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-6xl mx-auto px-4 py-16">
        {/* How It Works */}
        <div className="rewards-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Shop & Earn</h3>
              <p className="text-gray-700">
                Earn 1 point for every $1 spent on fragrances and accessories. No minimum purchase required.
              </p>
            </div>
            <div className="text-center bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Collect Points</h3>
              <p className="text-gray-700">
                Points are automatically added to your account after each purchase. Track your balance anytime.
              </p>
            </div>
            <div className="text-center bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Redeem Rewards</h3>
              <p className="text-gray-700">
                Use your points for discounts, free products, or exclusive member-only items and experiences.
              </p>
            </div>
          </div>
        </div>

        {/* Earning Points */}
        <div className="rewards-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Ways to Earn Points</h2>
          <div className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="text-2xl text-[#A88006] flex-shrink-0">💰</div>
                  <div>
                    <h4 className="text-black font-semibold">Purchase Products</h4>
                    <p className="text-gray-700 text-sm">1 point per $1 spent</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl text-[#A88006] flex-shrink-0">⭐</div>
                  <div>
                    <h4 className="text-black font-semibold">Write Reviews</h4>
                    <p className="text-gray-700 text-sm">50 points per product review</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl text-[#A88006] flex-shrink-0">🎁</div>
                  <div>
                    <h4 className="text-black font-semibold">Birthday Bonus</h4>
                    <p className="text-gray-700 text-sm">200 points on your birthday</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="h-8 w-8 text-[#A88006] flex-shrink-0 text-2xl">📧</div>
                  <div>
                    <h4 className="text-black font-semibold">Newsletter Signup</h4>
                    <p className="text-gray-700 text-sm">100 points for joining</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-8 w-8 text-[#A88006] flex-shrink-0 text-2xl">👥</div>
                  <div>
                    <h4 className="text-black font-semibold">Refer Friends</h4>
                    <p className="text-gray-700 text-sm">500 points per successful referral</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-8 w-8 text-[#A88006] flex-shrink-0 text-2xl">📱</div>
                  <div>
                    <h4 className="text-black font-semibold">Social Media Follow</h4>
                    <p className="text-gray-700 text-sm">25 points per platform</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reward Tiers */}
        <div className="rewards-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Membership Tiers</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🥉</span>
                </div>
                <h3 className="text-xl font-bold text-black">Bronze</h3>
                <p className="text-gray-600 text-sm">$0 - $499 spent</p>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 1 point per $1 spent</li>
                <li>• Birthday bonus: 200 points</li>
                <li>• Early sale notifications</li>
                <li>• Free standard shipping on $50+</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#A88006]/20 to-[#9F7800]/20 p-6 rounded-xl border border-[#A88006]/40 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#A88006] text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#A88006] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🥈</span>
                </div>
                <h3 className="text-xl font-bold text-black">Silver</h3>
                <p className="text-gray-600 text-sm">$500 - $1,499 spent</p>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 1.25 points per $1 spent</li>
                <li>• Birthday bonus: 300 points</li>
                <li>• Exclusive member discounts</li>
                <li>• Free shipping on all orders</li>
                <li>• Priority customer support</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🥇</span>
                </div>
                <h3 className="text-xl font-bold text-black">Gold</h3>
                <p className="text-gray-600 text-sm">$1,500+ spent</p>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 1.5 points per $1 spent</li>
                <li>• Birthday bonus: 500 points</li>
                <li>• VIP exclusive products</li>
                <li>• Free expedited shipping</li>
                <li>• Personal fragrance consultant</li>
                <li>• Annual surprise gift</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Redemption Options */}
        <div className="rewards-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Redeem Your Points</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-3xl mb-4">💰</div>
              <h4 className="text-black font-semibold mb-2">$5 Off</h4>
              <p className="text-[#A88006] font-bold mb-2">500 Points</p>
              <p className="text-gray-600 text-sm">Discount on your next order</p>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-3xl mb-4">💵</div>
              <h4 className="text-black font-semibold mb-2">$10 Off</h4>
              <p className="text-[#A88006] font-bold mb-2">1,000 Points</p>
              <p className="text-gray-600 text-sm">Discount on your next order</p>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-3xl mb-4">🎁</div>
              <h4 className="text-black font-semibold mb-2">Free Sample Set</h4>
              <p className="text-[#A88006] font-bold mb-2">750 Points</p>
              <p className="text-gray-600 text-sm">5 premium fragrance samples</p>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="text-3xl mb-4">🚚</div>
              <h4 className="text-black font-semibold mb-2">Free Shipping</h4>
              <p className="text-[#A88006] font-bold mb-2">300 Points</p>
              <p className="text-gray-600 text-sm">On your next order</p>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="rewards-section bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
          <h2 className="text-2xl font-bold text-[#A88006] mb-6">Program Terms & Conditions</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700 text-sm">
            <div>
              <ul className="space-y-2">
                <li>• Points expire 12 months after earning date</li>
                <li>• Minimum 100 points required for redemption</li>
                <li>• Points cannot be transferred between accounts</li>
                <li>• Refunded orders will have points deducted</li>
              </ul>
            </div>
            <div>
              <ul className="space-y-2">
                <li>• Program benefits may change with notice</li>
                <li>• Account must be in good standing</li>
                <li>• Points have no cash value</li>
                <li>• One account per customer allowed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Join CTA */}
        <div className="rewards-section text-center bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg mt-16">
          <h3 className="text-2xl font-bold text-black mb-4">Ready to Start Earning?</h3>
          <p className="text-gray-700 mb-6">
            Create an account today and start earning points with your first purchase!
          </p>
          <button className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-8 py-3 rounded-lg hover:opacity-90 transition-all">
            Join Rewards Program
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RewardsProgram;